import { useEffect, useRef, useState } from 'react';

import { loadGoogleMapsLibraries } from '../../../../hooks/useGoogleMapsPlaces';
import { getAddressString } from '../../utils/locations';
import { AVAILABILITY_TONES, getAvailabilityTone } from '../../utils/goLocker';

import styles from './GoLocker.module.scss';

// Pin fills per availability (Figma GoLocker/Map Pin 12233:3329). Literals, not CSS
// vars: they are painted into an SVG data URI that cannot read the cascade. Each
// mirrors a :root token — success, warning, neutral-400, neutral-900.
const PIN_COLORS = {
  [AVAILABILITY_TONES.AVAILABLE]: { fill: '#2B8747', text: '#FFFFFF' },
  [AVAILABILITY_TONES.FEW_LEFT]: { fill: '#FFDD42', text: '#101010' },
  [AVAILABILITY_TONES.FULL]: { fill: '#A9A9A9', text: '#FFFFFF' },
  // No live answer (E4) or not loaded yet: the black pin from frame 12256:5654.
  [AVAILABILITY_TONES.UNKNOWN]: { fill: '#101010', text: '#FFFFFF' },
};

// Home pin: magenta (#ff4286, Figma accent/magenta) circle with the House icon.
const HOME_PIN_COLOR = '#FF4286';

const PIN_SIZE = 32;
const HOME_PIN_SIZE = 40;

const svgUrl = (svg) => `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;

export const buildPinSvg = (number, tone) => {
  const { fill, text } = PIN_COLORS[tone] ?? PIN_COLORS[AVAILABILITY_TONES.UNKNOWN];
  return `<svg xmlns="http://www.w3.org/2000/svg" width="${PIN_SIZE}" height="${PIN_SIZE}" viewBox="0 0 32 32">`
    + `<circle cx="16" cy="16" r="15" fill="${fill}" stroke="#FFFFFF" stroke-width="2"/>`
    + `<text x="16" y="20.5" text-anchor="middle" font-family="Albert Sans, Arial, sans-serif" font-size="13" font-weight="700" fill="${text}">${number}</text>`
    + '</svg>';
};

const HOME_PIN_SVG = `<svg xmlns="http://www.w3.org/2000/svg" width="${HOME_PIN_SIZE}" height="${HOME_PIN_SIZE}" viewBox="0 0 40 40">`
  + `<circle cx="20" cy="20" r="20" fill="${HOME_PIN_COLOR}"/>`
  + '<g transform="translate(8 8)" fill="none" stroke="#FFFFFF" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round">'
  + '<path d="M12 12H12.01"/>'
  + '<path d="M15 21V13a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v8"/>'
  + '<path d="M3 10a2 2 0 0 1 .709-1.528l7-6a2 2 0 0 1 2.582 0l7 6A2 2 0 0 1 21 10v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>'
  + '</g></svg>';

// Google's "night" palette, tuned to the dark basemap in Figma 12229:3234. A classic
// styles array (no mapId), so it needs no Cloud map style and works with the key the
// app already ships for Places.
const DARK_MAP_STYLES = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
];

const hasCoordinates = (location) => (
  Number.isFinite(Number(location?.coordinates?.lat))
  && Number.isFinite(Number(location?.coordinates?.lng))
);

// Geocoding the customer's address is best-effort: the home pin is context, not
// content. A key without the Geocoding API, or an address Google cannot place,
// just drops the pin.
//
// Every Geocoder request is billed, and the map redraws whenever `locations` or
// `address` changes (at least twice per open: preview rows, then the live answer),
// so the answer — a place or null — is kept per address string for the session.
// The promise is stored, so redraws racing the first request share it.
const geocodedByAddress = new Map();

const geocodeAddress = (address) => {
  const query = getAddressString(address);
  const { Geocoder } = window.google?.maps ?? {};
  // Nothing was asked of Google, so nothing to remember.
  if (!query || !Geocoder) return Promise.resolve(null);

  const cached = geocodedByAddress.get(query);
  if (cached) return cached;

  const request = new Promise((resolve) => {
    try {
      new Geocoder().geocode({ address: query }, (results, status) => {
        resolve(status === 'OK' && results?.[0]?.geometry?.location ? results[0].geometry.location : null);
      });
    } catch (e) {
      resolve(null);
    }
  });
  geocodedByAddress.set(query, request);
  return request;
};

/**
 * GoLockerMap — numbered pins (1..N, coloured by live availability) plus a home pin
 * for the customer's address (Figma G2 12229:3234, E4, E6).
 *
 * Classic `google.maps.Marker` with SVG icons rather than AdvancedMarkerElement: the
 * advanced marker needs a Cloud `mapId`, which this project does not have.
 *
 * Degrades to NOTHING — the caller's list still renders — when the Maps library
 * fails to load, the key is rejected (`gm_authFailure`), or no bank has coordinates.
 * The pin numbers match the list rows, so the list alone is a complete answer.
 */
const GoLockerMap = ({ locations = [], address }) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const [failed, setFailed] = useState(false);
  const [ready, setReady] = useState(false);

  const mappable = locations.filter(hasCoordinates);

  useEffect(() => {
    let active = true;
    // Google calls this global when the key is invalid or the Maps JS API is not
    // enabled for it — the map then renders its own "Oops" overlay, so hide it.
    const previousAuthFailure = window.gm_authFailure;
    window.gm_authFailure = () => {
      if (active) setFailed(true);
      if (typeof previousAuthFailure === 'function') previousAuthFailure();
    };

    loadGoogleMapsLibraries(['maps', 'geocoding']).then((ok) => {
      if (!active) return;
      if (!ok || !window.google?.maps?.Map) {
        setFailed(true);
        return;
      }
      setReady(true);
    });

    return () => {
      active = false;
      window.gm_authFailure = previousAuthFailure;
    };
  }, []);

  // Draw (and redraw when availability lands — the pin colours change).
  useEffect(() => {
    if (!ready || failed || !containerRef.current || mappable.length === 0) return undefined;
    let active = true;
    const { maps } = window.google;

    try {
      if (!mapRef.current) {
        mapRef.current = new maps.Map(containerRef.current, {
          disableDefaultUI: true,
          gestureHandling: 'cooperative',
          clickableIcons: false,
          keyboardShortcuts: false,
          styles: DARK_MAP_STYLES,
        });
      }
    } catch (e) {
      setFailed(true);
      return undefined;
    }

    const map = mapRef.current;
    markersRef.current.forEach((marker) => marker.setMap(null));
    markersRef.current = [];

    const bounds = new maps.LatLngBounds();
    mappable.forEach((location) => {
      const position = {
        lat: Number(location.coordinates.lat),
        lng: Number(location.coordinates.lng),
      };
      const number = locations.indexOf(location) + 1;
      bounds.extend(position);
      markersRef.current.push(new maps.Marker({
        map,
        position,
        title: `${number}`,
        icon: {
          url: svgUrl(buildPinSvg(number, getAvailabilityTone(location.availability))),
          scaledSize: new maps.Size(PIN_SIZE, PIN_SIZE),
          anchor: new maps.Point(PIN_SIZE / 2, PIN_SIZE / 2),
        },
      }));
    });

    // One bank: fitBounds on a single point zooms to street level, so pin a
    // neighbourhood zoom instead. The home pin below re-fits to both.
    if (mappable.length === 1) {
      map.setCenter(bounds.getCenter());
      map.setZoom(15);
    } else {
      map.fitBounds(bounds, 32);
    }

    geocodeAddress(address).then((home) => {
      if (!active || !home) return;
      markersRef.current.push(new maps.Marker({
        map,
        position: home,
        title: 'Your address',
        zIndex: 0,
        icon: {
          url: svgUrl(HOME_PIN_SVG),
          scaledSize: new maps.Size(HOME_PIN_SIZE, HOME_PIN_SIZE),
          anchor: new maps.Point(HOME_PIN_SIZE / 2, HOME_PIN_SIZE / 2),
        },
      }));
      bounds.extend(home);
      map.fitBounds(bounds, 32);
    });

    return () => { active = false; };
  }, [ready, failed, locations, address]);

  if (failed || mappable.length === 0) return null;

  return (
    <div
      ref={containerRef}
      className={styles.map}
      role="img"
      aria-label={`Map of ${mappable.length} nearby GoLockers`}
    />
  );
};

export default GoLockerMap;
